import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import { useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { v4 } from 'uuid';
import { RequestParameters } from '~/backend/backend-client';
import useProcessDicomFiles from '~/hooks/useProcessDicomFiles';
import {
    ISRO_MAX_SAMPLES,
    ISRO_MIN_SAMPLES,
    isIsroModel,
    isroDeploymentEnabled,
    isroResultFrom,
    isValidIsroSampleCount,
} from '~/hooks/workflows/isro/isroSamples';
import { registrationSeriesDescription } from '~/hooks/workflows/dvf/deformableRegistrationField';
import {
    isroResultState,
    isroSelectedSampleNumberState,
} from '~/state/atoms/isroResultAtoms';
import { Image3D } from '~/domain/patient/image3d';
import { useFuseGeneratedImage } from '~/hooks/workflows/useFuseGeneratedImage';
import { Job } from '~/state/atoms/jobAtoms';
import {
    WorkflowTypes,
    pseudonymizeState,
    workflowModalState,
} from '~/state/atoms/viewerAtoms';
import { handleError } from '~/utils/errors';
import { PredictionModel } from '~/utils/transforms/predictionModelsDataTransforms';
import { NewJobData, useJobManagement } from '../../useJobManagement';
import useAnonymizer from '../useAnonymizer';
import { useSessionID, useTranslationBackend } from '../useBackend';

/**
 * A React hook for creating an image translation task.
 * @returns {Object} - An object containing the start function and submission state.
 */
export function useImageTranslation() {
    const primaryImage = useSelectedPrimaryImage();
    const pseudonymize = useRecoilValue(pseudonymizeState);
    const setWorkflowModalState = useSetRecoilState(workflowModalState);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const imageTranslationClient = useTranslationBackend();
    const { addJob, updateJob } = useJobManagement();
    const { anonymizeBlobs, reidentifyFiles } = useAnonymizer();
    const { resetSessionID, sessionID } = useSessionID();
    const { processCreatedFiles } = useProcessDicomFiles();
    const fuseGeneratedImage = useFuseGeneratedImage();
    const setIsroResult = useSetRecoilState(isroResultState);
    const setIsroSelectedSample = useSetRecoilState(
        isroSelectedSampleNumberState
    );
    // The reference is the scan the run was given, fixed when it starts.
    const handleProcessedFiles =
        (referenceSeriesUid: string) => async (job: Job) => {
            if (job.results && job.results?.length > 0) {
                const finalFiles = job.payload?.pseudonymized
                    ? await reidentifyFiles(job.results)
                    : job.results;

                if (!finalFiles) {
                    handleError(
                        'Re-identification failed for one or more files.'
                    );
                    return;
                }

                const processedItems = await processCreatedFiles(
                    finalFiles,
                    job.task!.model
                );
                if (!processedItems) {
                    return;
                }

                updateJob(job.id, { isImported: true });

                const model = job.task?.model;
                if (isroDeploymentEnabled() && isIsroModel(model)) {
                    const result = isroResultFrom({
                        jobId: job.id,
                        model,
                        referenceSeriesUid,
                        images: processedItems.images,
                        registrations:
                            processedItems.deformableRegistrations.map(
                                (registration) => ({
                                    sopInstanceUid: registration.sopInstanceUid,
                                    seriesDescription:
                                        registrationSeriesDescription(
                                            registration
                                        ),
                                })
                            ),
                    });
                    if (!result) {
                        handleError(
                            `${model.name} returned no recognisable samples. Expected each generated series description to carry the model name or label.`
                        );
                        return;
                    }
                    setIsroSelectedSample(null);
                    setIsroResult(result);
                    return;
                }

                fuseGeneratedImage(processedItems);
            }
        };

    const handleSubmit = async (
        selectedModel: PredictionModel,
        customizationName?: string,
        numSamples?: number,
        image?: Image3D
    ) => {
        if (isSubmitting) {
            return;
        }
        // Rejected, never clamped: a changed count would silently differ from the request.
        if (numSamples !== undefined && !isValidIsroSampleCount(numSamples)) {
            handleError(
                `Invalid number of samples: ${numSamples}. Expected an integer between ${ISRO_MIN_SAMPLES} and ${ISRO_MAX_SAMPLES}.`
            );
            return;
        }

        const sourceImage = image ?? primaryImage;
        if (sourceImage) {
            setIsSubmitting(true);
            try {
                resetSessionID();
                const taskUID = `${sessionID}.${v4().split('-')[0]}`;
                const blobs = Array.from(sourceImage.getSlicesAsDicomBlobs());
                const blobsToUpload = pseudonymize
                    ? await anonymizeBlobs(taskUID, blobs)
                    : blobs;

                const modelIdentifier = customizationName
                    ? `${selectedModel.name}.${customizationName}`
                    : selectedModel.name;

                const backendParameters: RequestParameters = {
                    action: modelIdentifier,
                    taskUID,
                    nExpectedFiles: blobs.length.toString(),
                };

                if (numSamples !== undefined) {
                    // Spread verbatim into headers: the key must match the backend's `numSamples` alias.
                    backendParameters.numSamples = numSamples.toString();
                }

                if (blobsToUpload) {
                    const newJobDetails: NewJobData = {
                        id: sessionID,
                        name: 'Image+',
                        totalItems: blobsToUpload.length,
                        type: WorkflowTypes.IMAGE,
                        client: imageTranslationClient,
                        onLoad: handleProcessedFiles(sourceImage.seriesUid),
                        payload: {
                            filesToUpload: blobsToUpload.map((file) => ({
                                file,
                            })),
                            backendParameters,
                            pseudonymized: pseudonymize,
                        },
                        task: {
                            model: selectedModel,
                        },
                    };

                    addJob(newJobDetails);
                    setWorkflowModalState((state) => ({
                        ...state,
                        open: false,
                    }));
                } else {
                    handleError('Anonymization failed for one or more files.');
                }
            } catch (error) {
                handleError(error, 'Failed to start image translation task.');
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    return { startProcessing: handleSubmit, isSubmitting };
}
